import React, { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PhoneCall, Target, PenLine, KeyRound, User, Image as ImageIcon, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [10, 20, 30, 50];

function BeldoelSectie() {
  const { user, setUser } = useAuth();
  const [val, setVal] = useState(String(user?.beldoel ?? 20));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const n = Math.max(0, Math.min(1000, parseInt(val, 10) || 0));
    setBusy(true);
    try {
      const { data } = await api.put("/instellingen/beldoel", { beldoel: n });
      setVal(String(data.beldoel));
      setUser?.((u) => (u ? { ...u, beldoel: data.beldoel } : u));
      toast.success(`Beldoel ingesteld op ${data.beldoel} per dag`);
    } catch {
      toast.error("Opslaan mislukt");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-5" data-testid="account-beldoel">
      <div className="flex items-center gap-2">
        <Target className="h-5 w-5 text-primary" />
        <h2 className="font-medium text-lg">Dagelijks beldoel</h2>
      </div>
      <p className="text-sm text-muted-foreground -mt-2">
        Bepaalt je voortgangsbalk op het dashboard en de Belactiviteit-pagina.
      </p>
      <div className="space-y-2">
        <Label htmlFor="beldoel">Belletjes per dag</Label>
        <Input id="beldoel" type="number" min={0} max={1000} value={val}
          onChange={(e) => setVal(e.target.value)} className="w-40" data-testid="beldoel-input" />
        <div className="flex gap-2 pt-1">
          {PRESETS.map((p) => (
            <button key={p} type="button" onClick={() => setVal(String(p))}
              className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${String(p) === val ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}
              data-testid={`beldoel-preset-${p}`}>
              {p}
            </button>
          ))}
        </div>
      </div>
      <Button onClick={save} disabled={busy} data-testid="beldoel-save">
        <PhoneCall className="h-4 w-4 mr-1" /> {busy ? "Bezig…" : "Beldoel opslaan"}
      </Button>
    </div>
  );
}

function HandtekeningSectie() {
  const { user, setUser } = useAuth();
  const [val, setVal] = useState(user?.handtekening ?? "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/instellingen/handtekening", { handtekening: val });
      setVal(data.handtekening);
      setUser?.((u) => (u ? { ...u, handtekening: data.handtekening } : u));
      toast.success("Handtekening opgeslagen");
    } catch {
      toast.error("Opslaan mislukt");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="account-handtekening">
      <div className="flex items-center gap-2">
        <PenLine className="h-5 w-5 text-primary" />
        <h2 className="font-medium text-lg">E-mailhandtekening</h2>
      </div>
      <p className="text-sm text-muted-foreground -mt-2">
        Deze handtekening wordt onderaan al je verstuurde e-mails toegevoegd. Het NextProduct-logo
        wordt automatisch klein onder je handtekening geplaatst.
      </p>
      <Textarea rows={5} value={val} onChange={(e) => setVal(e.target.value)}
        placeholder={"Met vriendelijke groet,\n\n" + (user?.naam || "Jouw naam") + "\nNextProduct\nTel. ..."}
        data-testid="handtekening-input" />
      <Button onClick={save} disabled={busy} data-testid="handtekening-save">
        <PenLine className="h-4 w-4 mr-1" /> {busy ? "Bezig…" : "Handtekening opslaan"}
      </Button>
    </div>
  );
}

function LogoSectie() {
  const { user, setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [bust, setBust] = useState(Date.now());
  const fileRef = React.useRef(null);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const heeftLogo = !!user?.handtekening_logo_url;
  const logoSrc = heeftLogo ? `${BACKEND}${user.handtekening_logo_url}?t=${bust}` : null;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/instellingen/handtekening-logo", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setUser?.((u) => (u ? { ...u, handtekening_logo_url: data.handtekening_logo_url } : u));
      setBust(Date.now());
      toast.success("Logo bijgewerkt");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload mislukt");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const verwijder = async () => {
    setBusy(true);
    try {
      await api.delete("/instellingen/handtekening-logo");
      setUser?.((u) => (u ? { ...u, handtekening_logo_url: "" } : u));
      toast.success("Eigen logo verwijderd — standaard NextProduct-logo wordt gebruikt");
    } catch {
      toast.error("Verwijderen mislukt");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="account-logo">
      <div className="flex items-center gap-2">
        <ImageIcon className="h-5 w-5 text-primary" />
        <h2 className="font-medium text-lg">Logo in handtekening</h2>
      </div>
      <p className="text-sm text-muted-foreground -mt-2">
        Dit logo verschijnt klein onderaan je e-mails. Zonder eigen logo wordt het standaard
        NextProduct-logo gebruikt. PNG/JPG/WEBP, max. 2 MB.
      </p>
      <div className="flex items-center gap-4">
        <div className="h-16 w-40 rounded-lg border border-border bg-secondary/30 flex items-center justify-center overflow-hidden" data-testid="account-logo-preview">
          {logoSrc
            ? <img src={logoSrc} alt="Handtekening-logo" className="max-h-12 max-w-[140px] object-contain" />
            : <span className="text-xs text-muted-foreground px-2 text-center">Standaard NextProduct-logo</span>}
        </div>
        <div className="space-y-2">
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif"
            onChange={upload} className="hidden" data-testid="logo-file-input" />
          <Button variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="logo-upload-btn">
            <Upload className="h-4 w-4 mr-1" /> {busy ? "Bezig…" : (heeftLogo ? "Logo vervangen" : "Logo uploaden")}
          </Button>
          {heeftLogo && (
            <Button variant="ghost" disabled={busy} onClick={verwijder}
              className="text-destructive hover:text-destructive block" data-testid="logo-remove-btn">
              <Trash2 className="h-4 w-4 mr-1" /> Verwijderen
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function WachtwoordSectie() {
  const [huidig, setHuidig] = useState("");
  const [nieuw, setNieuw] = useState("");
  const [herhaal, setHerhaal] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (nieuw.length < 6) { toast.error("Nieuw wachtwoord moet minstens 6 tekens zijn"); return; }
    if (nieuw !== herhaal) { toast.error("Wachtwoorden komen niet overeen"); return; }
    setBusy(true);
    try {
      await api.post("/auth/wachtwoord-wijzigen", { huidig_wachtwoord: huidig, nieuw_wachtwoord: nieuw });
      toast.success("Wachtwoord gewijzigd");
      setHuidig(""); setNieuw(""); setHerhaal("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wijzigen mislukt");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="account-wachtwoord">
      <div className="flex items-center gap-2">
        <KeyRound className="h-5 w-5 text-primary" />
        <h2 className="font-medium text-lg">Wachtwoord wijzigen</h2>
      </div>
      <div className="grid gap-3 max-w-sm">
        <div className="space-y-1">
          <Label htmlFor="huidig">Huidig wachtwoord</Label>
          <Input id="huidig" type="password" value={huidig} onChange={(e) => setHuidig(e.target.value)} data-testid="wachtwoord-huidig" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="nieuw">Nieuw wachtwoord</Label>
          <Input id="nieuw" type="password" value={nieuw} onChange={(e) => setNieuw(e.target.value)} data-testid="wachtwoord-nieuw" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="herhaal">Herhaal nieuw wachtwoord</Label>
          <Input id="herhaal" type="password" value={herhaal} onChange={(e) => setHerhaal(e.target.value)} data-testid="wachtwoord-herhaal" />
        </div>
      </div>
      <Button onClick={save} disabled={busy || !huidig || !nieuw} data-testid="wachtwoord-save">
        <KeyRound className="h-4 w-4 mr-1" /> {busy ? "Bezig…" : "Wachtwoord wijzigen"}
      </Button>
    </div>
  );
}

export default function InstellingenAccount() {
  const { user } = useAuth();
  return (
    <div className="space-y-8 max-w-xl" data-testid="instellingen-account-page">
      <div>
        <div className="text-sm text-muted-foreground">Instellingen</div>
        <h1 className="font-display text-4xl font-medium tracking-tight mt-1">Mijn account</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Beheer je persoonlijke instellingen: beldoel, e-mailhandtekening en wachtwoord.
        </p>
      </div>

      <div className="bg-card border border-border rounded-xl p-5 flex items-center gap-3" data-testid="account-info">
        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <User className="h-5 w-5 text-primary" />
        </div>
        <div>
          <div className="font-medium">{user?.naam || "Mijn account"}</div>
          <div className="text-xs text-muted-foreground">{user?.email} · {user?.rol === "admin" ? "Beheerder" : "Gebruiker"}</div>
        </div>
      </div>

      <BeldoelSectie />
      <HandtekeningSectie />
      <LogoSectie />
      <WachtwoordSectie />
    </div>
  );
}
